import { Button, Dialog } from '@cloudflare/kumo';
import { Select } from '@cloudflare/kumo/components/select';
import { ArrowLeft, CreditCard } from '@phosphor-icons/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { paymentMethodBrand } from '../../shared/billing.ts';
import { ownerLabel } from '../../shared/contracts.ts';
import { accountsQuery, api, queryClient, unwrap } from '../api.ts';
import { ErrorNotice, Loading } from '../common.tsx';
import { BillingPayment } from '../components/billing-payment.tsx';
import { PaymentMethodSetup } from '../components/payment-method-setup.tsx';
import { SharingControls } from '../components/sharing-controls.tsx';
import { SiteBilling } from '../components/site-billing.tsx';
import { Route as parent } from './signed-in.tsx';
export const Route = createRoute({
  getParentRoute: () => parent,
  path: '/billing',
  validateSearch: z.object({
    account: z.string().optional(),
    operation: z.string().optional(),
  }).parse,
  component: BillingPage,
});

function BillingPage() {
  const accounts = useQuery(accountsQuery);
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const accountId =
    search.account ?? accounts.data?.find((account) => account.owner)?.id ?? accounts.data?.[0]?.id;
  if (accounts.isPending) return <Loading />;
  if (accounts.error) return <ErrorNotice error={accounts.error} />;
  return (
    <main className="m-auto px-4.5 py-6 max-w-300 [&_>_.panel-heading]:mx-0 [&_>_.panel-heading]:my-7.5 [&_>_.panel-heading]:items-stretch [&_>_.panel-heading]:flex-col [&_>_.panel-heading]:gap-4 [&_h1]:m-0 [&_h1]:text-[28px] [&_h1]:leading-[1.2] [&_h1]:font-semibold [&_h1]:tracking-[-0.8px] [&_h2]:mx-0 [&_h2]:mt-0 [&_h2]:mb-3.5 [&_h2]:text-[19px] [&_h2]:font-semibold [&_h2]:tracking-[-0.35px] [&_h3]:m-0 [&_h3]:text-[16px] [&_h3]:font-semibold [&_h3]:flex [&_h3]:items-center [&_h3]:gap-2 [&_label]:text-[13px] [&_.activity-table_a]:underline min-[721px]:px-8 min-[721px]:pt-8.5 min-[721px]:pb-20 min-[721px]:[&_>_.panel-heading]:items-start min-[721px]:[&_>_.panel-heading]:flex-row min-[721px]:[&_>_.panel-heading]:gap-5">
      <Link
        to="/"
        className="back-link inline-flex items-center gap-2 text-kumo-subtle text-[13px] mb-4 [&:hover]:text-kumo-default"
      >
        <ArrowLeft />
        My sites
      </Link>
      <div className="panel-heading flex items-start justify-between gap-5 mb-[25px] [&_.muted]:mt-2 min-[761px]:items-center">
        <div>
          <h1 className="text-[30px] leading-[1.2] font-semibold tracking-[-0.8px]">Billing</h1>
          <p className="muted leading-[1.65] text-kumo-subtle text-[14px]">
            The base plan is $15 per month for each site.
          </p>
        </div>
        {accounts.data.length > 1 ? (
          <div>
            <Select<string>
              label="Customer account"
              className="w-full min-w-0"
              value={accountId ?? ''}
              onValueChange={(selectedValue) => {
                if (selectedValue === null) return;
                void navigate({ to: '/billing', search: { account: selectedValue } });
              }}
              items={Object.fromEntries(
                accounts.data.map((account) => [
                  account.id,
                  `${ownerLabel({ name: account.ownerName, email: account.ownerEmail })}${
                    account.owner ? '' : ' · Payment-method access'
                  }`,
                ]),
              )}
            />
          </div>
        ) : null}
      </div>
      {accountId ? (
        <AccountBilling key={accountId} accountId={accountId} returnedOperation={search.operation} />
      ) : (
        <p className="leading-[1.65]">No billing accounts are available.</p>
      )}
    </main>
  );
}

function AccountBilling({ accountId, returnedOperation }: { accountId: string; returnedOperation?: string }) {
  const setupSection = useRef<HTMLDivElement>(null);
  const [showSetup, setShowSetup] = useState(false);
  const checkedReturn = useRef<string | null>(null);
  const [payInvoice, setPayInvoice] = useState<string>();
  const [cancelRenewalSite, setCancelRenewalSite] = useState<{ id: string; name: string }>();
  const account = useQuery({
    queryKey: ['billing', accountId],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/accounts/{accountUserId}/billing', {
          params: { path: { accountUserId: accountId } },
        }),
      ),
    refetchInterval: (query) =>
      query.state.data?.operations.some((operation) =>
        ['Pending', 'Processing', 'AwaitingPaymentMethod', 'AwaitingPayment'].includes(operation.status),
      )
        ? 2000
        : false,
  });
  const invoices = useQuery({
    queryKey: ['invoices', accountId],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/accounts/{accountUserId}/invoices', {
          params: { path: { accountUserId: accountId } },
        }),
      ),
  });
  const refresh = useMutation({
    mutationFn: async (operationId: string) =>
      unwrap(
        await api.POST('/api/accounts/{accountUserId}/billing/{operationId}/refresh', {
          params: { path: { accountUserId: accountId, operationId } },
          body: {},
        }),
      ),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['billing', accountId] });
      await queryClient.invalidateQueries({ queryKey: ['invoices', accountId] });
    },
  });
  const { mutate: refreshReturned } = refresh;
  useEffect(() => {
    if (returnedOperation && checkedReturn.current !== returnedOperation) {
      checkedReturn.current = returnedOperation;
      refreshReturned(returnedOperation);
    }
  }, [returnedOperation, refreshReturned]);
  const change = useMutation({
    mutationFn: async (input: { kind: 'activate' | 'cancel' | 'restore'; siteId: string }) => {
      const idempotencyKey = crypto.randomUUID();
      const siteId = input.siteId;
      if (input.kind === 'activate')
        return unwrap(
          await api.POST('/api/sites/{siteId}/service', {
            params: { path: { siteId } },
            body: { idempotencyKey },
          }),
        );
      if (input.kind === 'cancel')
        return unwrap(
          await api.POST('/api/sites/{siteId}/service/cancel', {
            params: { path: { siteId } },
            body: { idempotencyKey },
          }),
        );
      return unwrap(
        await api.POST('/api/sites/{siteId}/service/restore', {
          params: { path: { siteId } },
          body: { idempotencyKey },
        }),
      );
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['billing', accountId] }),
  });
  if (account.isPending) return <Loading />;
  if (account.error) return <ErrorNotice error={account.error} />;
  const info = account.data;
  const pending = info.operations.filter((operation) =>
    ['Pending', 'Processing', 'AwaitingPaymentMethod', 'AwaitingPayment'].includes(operation.status),
  );
  const busy = change.isPending;
  const money = (cents: number, currency = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
  const date = (value: string) => new Date(value).toLocaleDateString(undefined, { dateStyle: 'long' });
  const dateTime = (value: string) =>
    new Date(value).toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' });
  const statusLabel: Record<string, string> = {
    Trialing: 'Free trial',
    Active: 'Subscription active',
    PastDue: 'Payment needs attention',
    Unpaid: 'Service suspended',
    Canceled: 'Subscription ended',
    Incomplete: 'Finish payment to activate',
    IncompleteExpired: 'Activation payment expired',
    Paused: 'Subscription paused',
  };
  return (
    <div className="grid grid-cols-1 gap-8">
      <ErrorNotice error={change.error ?? refresh.error} />
      {!info.owner ? (
        <p className="p-4.5 border border-kumo-line leading-[1.65] bg-kumo-tint rounded-[8px]">
          You can update the payment method for {ownerLabel({ name: info.ownerName, email: info.ownerEmail })}
          . That change applies to the whole subscription. The owner chooses which sites are billed.
        </p>
      ) : null}
      <section className="p-6 border border-kumo-line grid grid-cols-1 gap-8 rounded-[12px] bg-kumo-base [&_p]:mx-0 [&_p]:my-3 [&_p]:leading-[1.65] [&_p]:text-[14px] min-[721px]:grid-cols-[1fr_1fr]">
        <div>
          <h2 className="text-[20px] font-semibold tracking-[-0.35px]">
            {info.stripeSubscriptionStatus === null
              ? 'No subscription yet'
              : (statusLabel[info.stripeSubscriptionStatus] ?? 'Billing status unavailable')}
          </h2>
          {info.trialEndsAt && info.stripeSubscriptionStatus === 'Trialing' ? (
            <p className="leading-[1.65]">
              Your account’s trial ends {dateTime(info.trialEndsAt)}. Included sites then cost $15 each per
              month.
            </p>
          ) : (
            [null, 'Incomplete', 'IncompleteExpired'].includes(info.stripeSubscriptionStatus) && (
              <p className="leading-[1.65]">Building, editing, and previewing drafts is free.</p>
            )
          )}
          {info.trialEligible && (
            <p className="leading-[1.65]">
              You get a <strong>free three-day trial</strong>. A payment method is required.
            </p>
          )}
        </div>
        <div ref={setupSection}>
          <h3 className="text-[16px] font-semibold">
            <CreditCard />
            Payment method
          </h3>
          {info.paymentMethod ? (
            <p className="leading-[1.65]">
              {paymentMethodBrand(info.paymentMethod.brand)} ending {info.paymentMethod.last4} · expires{' '}
              {info.paymentMethod.expMonth}/{info.paymentMethod.expYear}
            </p>
          ) : (
            <p className="leading-[1.65]">No payment method saved.</p>
          )}
          <Button onClick={() => setShowSetup(true)} disabled={showSetup}>
            {info.paymentMethod ? 'Update payment method' : 'Add payment method'}
          </Button>
          {showSetup ? (
            <div className="mt-4 grid gap-4">
              <p>
                Saving a payment method does not start a trial or activate a site. Choose a site below after
                saving.
              </p>
              <PaymentMethodSetup accountId={accountId} onSaved={() => setShowSetup(false)} />
            </div>
          ) : null}
        </div>
      </section>
      <section>
        <h2 className="text-[20px] font-semibold tracking-[-0.35px]">Sites on this account</h2>
        <div className="my-4.5">
          {info.sites.map((site) => (
            <SiteBilling
              key={site.id}
              account={info}
              site={site}
              disabled={busy}
              changingKind={busy && change.variables.siteId === site.id ? change.variables.kind : undefined}
              onActivate={() => {
                if (info.paymentMethod) change.mutate({ kind: 'activate', siteId: site.id });
                else {
                  setShowSetup(true);
                  setupSection.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              onRestore={() => change.mutate({ kind: 'restore', siteId: site.id })}
              onCancelRenewal={() => {
                change.reset();
                setCancelRenewalSite(site);
              }}
            />
          ))}
        </div>
      </section>
      <section>
        <h2 className="text-[20px] font-semibold tracking-[-0.35px]">Invoices</h2>
        <ErrorNotice error={invoices.error} />
        {invoices.isPending ? (
          <Loading />
        ) : !invoices.data?.length ? (
          <p className="muted leading-[1.65] text-kumo-subtle text-[14px]">No invoices yet.</p>
        ) : (
          <div className="activity-table overflow-x-auto [&_table]:w-full [&_table]:mt-6 [&_table]:text-left [&_th]:p-3 [&_th]:border-b [&_th]:border-b-kumo-line [&_td]:p-3 [&_td]:border-b [&_td]:border-b-kumo-line">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Invoice</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th>Links</th>
                </tr>
              </thead>
              <tbody>
                {invoices.data.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>{date(invoice.createdAt)}</td>
                    <td>{invoice.number ?? 'Pending number'}</td>
                    <td>{invoice.status}</td>
                    <td>{money(invoice.amountDue, invoice.currency)}</td>
                    <td>
                      {invoice.status === 'open' &&
                      !pending.some((operation) => operation.invoiceId === invoice.id) ? (
                        <Button size="sm" onClick={() => setPayInvoice(invoice.id)}>
                          Pay invoice
                        </Button>
                      ) : null}
                      {invoice.pdfUrl ? (
                        <>
                          {' '}
                          ·{' '}
                          <a href={invoice.pdfUrl} target="_blank" rel="noreferrer">
                            PDF
                          </a>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {payInvoice ? (
          <section className="content-section border border-kumo-line bg-kumo-base rounded-[11px] mb-5 overflow-hidden">
            <div className="panel-heading flex items-start justify-between gap-5 mb-[25px] [&_.muted]:mt-2 min-[761px]:items-center">
              <h3 className="text-[16px] font-semibold">Pay invoice</h3>
              <Button variant="ghost" onClick={() => setPayInvoice(undefined)}>
                Close
              </Button>
            </div>
            <BillingPayment
              key={payInvoice}
              accountId={accountId}
              invoiceId={payInvoice}
              onComplete={() => setPayInvoice(undefined)}
            />
          </section>
        ) : null}
      </section>
      <SharingControls target={{ accountId }} />
      <Dialog.Root
        open={Boolean(cancelRenewalSite)}
        onOpenChange={(open) => {
          if (!open && !change.isPending) setCancelRenewalSite(undefined);
        }}
      >
        <Dialog size="lg" className="grid gap-5 p-6">
          <Dialog.Title className="text-xl font-semibold">Cancel renewal?</Dialog.Title>
          <Dialog.Description className="text-kumo-subtle">
            {cancelRenewalSite?.name} will stay online until its current trial or paid period ends. After
            that, we will stop hosting this site and visitors will no longer be able to access it.
          </Dialog.Description>
          <ErrorNotice error={change.error} />
          <div className="flex flex-wrap justify-end gap-3">
            <Button disabled={change.isPending} onClick={() => setCancelRenewalSite(undefined)}>
              Keep renewal
            </Button>
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => {
                if (cancelRenewalSite)
                  change.mutate(
                    { kind: 'cancel', siteId: cancelRenewalSite.id },
                    { onSuccess: () => setCancelRenewalSite(undefined) },
                  );
              }}
            >
              {change.isPending ? 'Canceling renewal…' : 'Cancel renewal'}
            </Button>
          </div>
        </Dialog>
      </Dialog.Root>
    </div>
  );
}
